<script setup lang="ts">
import { DialogContent, DialogOverlay, DialogPortal, DialogRoot, DialogTitle } from 'reka-ui'
import { computed, nextTick, ref, useId, watch } from 'vue'

import EIcon from './EIcon.vue'
import { fuzzySearch } from '../lib/fuzzy'
import { formatBinding } from '../lib/keys'
import { useUiStrings } from '../lib/strings'
import type { Command } from './types'

/**
 * The Command-K palette (CLAUDE.md §11): navigate, create anything, run a
 * report, hand a command to the Assistant — from one keystroke, anywhere.
 *
 * It is a shell only. Commands are supplied by the app (which is where
 * permissions live, so a user never sees a command they cannot run) and running
 * one is the app's job. The palette fetches nothing.
 *
 * Accessibility: the WAI-ARIA combobox pattern. Focus stays on the input at all
 * times; the listbox is referenced by `aria-controls` and the highlighted option
 * by `aria-activedescendant`, which is how a screen-reader user hears the
 * selection change while continuing to type.
 */

const props = withDefaults(
  defineProps<{
    commands: readonly Command[]
    placeholder?: string
    emptyText?: string
    /** Maximum results rendered; the palette is a shortcut, not a browser. */
    limit?: number
    /** Footer hint row. Turn off in embedded contexts. */
    showFooter?: boolean
  }>(),
  {
    placeholder: '',
    emptyText: '',
    limit: 50,
    showFooter: true,
  },
)

const emit = defineEmits<{ select: [command: Command] }>()

const open = defineModel<boolean>('open', { default: false })

const strings = useUiStrings()

const resolvedPlaceholder = computed(() =>
  props.placeholder === '' ? strings.value.palettePlaceholder : props.placeholder,
)
const resolvedEmptyText = computed(() =>
  props.emptyText === '' ? strings.value.paletteEmpty : props.emptyText,
)

const query = ref('')
const activeIndex = ref(0)
const listId = useId()
const inputId = useId()
const listRef = ref<HTMLElement | null>(null)

const results = computed<Command[]>(() =>
  fuzzySearch(props.commands, query.value, {
    fields: (command) => [command.label, command.group, command.description, ...(command.keywords ?? [])],
    limit: props.limit,
  })
    .map((result) => result.item)
    .filter((command) => command.disabled !== true),
)

/** Contiguous groups, in result order, so ranking is never reshuffled. */
const sections = computed<{ label: string; commands: Command[] }[]>(() => {
  const out: { label: string; commands: Command[] }[] = []
  for (const command of results.value) {
    const label = command.group ?? ''
    const last = out[out.length - 1]
    if (last && last.label === label) last.commands.push(command)
    else out.push({ label, commands: [command] })
  }
  return out
})

/** Flat index of a command, used for highlight state and option ids. */
function indexOf(command: Command): number {
  return results.value.findIndex((candidate) => candidate.id === command.id)
}

const optionId = (index: number): string => `${listId}-option-${index}`

const activeId = computed(() =>
  results.value.length > 0 ? optionId(Math.min(activeIndex.value, results.value.length - 1)) : undefined,
)

watch(query, () => {
  activeIndex.value = 0
})

watch(open, (isOpen) => {
  if (!isOpen) return
  query.value = ''
  activeIndex.value = 0
})

function scrollActiveIntoView(): void {
  const element = listRef.value?.querySelector<HTMLElement>('[data-active="true"]')
  element?.scrollIntoView({ block: 'nearest' })
}

function move(delta: number): void {
  const count = results.value.length
  if (count === 0) return
  activeIndex.value = (activeIndex.value + delta + count) % count
  void nextTick(scrollActiveIntoView)
}

function choose(command: Command | undefined): void {
  if (!command) return
  emit('select', command)
  open.value = false
}

function onKeydown(event: KeyboardEvent): void {
  switch (event.key) {
    case 'ArrowDown':
      event.preventDefault()
      move(1)
      break
    case 'ArrowUp':
      event.preventDefault()
      move(-1)
      break
    case 'Home':
      event.preventDefault()
      activeIndex.value = 0
      void nextTick(scrollActiveIntoView)
      break
    case 'End':
      event.preventDefault()
      activeIndex.value = Math.max(results.value.length - 1, 0)
      void nextTick(scrollActiveIntoView)
      break
    case 'Enter':
      event.preventDefault()
      choose(results.value[activeIndex.value])
      break
    default:
      break
  }
}

const enterHint = computed(() => formatBinding('enter'))
</script>

<template>
  <DialogRoot v-model:open="open">
    <DialogPortal>
      <DialogOverlay class="e-palette__overlay" />
      <!-- The palette's own placeholder text describes it; no separate description node. -->
      <DialogContent class="e-palette" :aria-describedby="undefined">
        <DialogTitle class="e-visually-hidden">{{ strings.paletteTitle }}</DialogTitle>

        <div class="e-palette__search">
          <EIcon name="search" :size="16" class="e-palette__search-icon" />
          <input
            :id="inputId"
            v-model="query"
            class="e-palette__input"
            type="text"
            role="combobox"
            autocomplete="off"
            spellcheck="false"
            :placeholder="resolvedPlaceholder"
            aria-expanded="true"
            aria-autocomplete="list"
            :aria-controls="listId"
            :aria-activedescendant="activeId"
            @keydown="onKeydown"
          />
        </div>

        <div :id="listId" ref="listRef" class="e-palette__list" role="listbox" :aria-label="strings.paletteListLabel">
          <p v-if="results.length === 0" class="e-palette__empty">{{ resolvedEmptyText }}</p>

          <template v-for="section in sections" :key="section.label || 'ungrouped'">
            <p v-if="section.label !== ''" class="e-palette__group" role="presentation">
              {{ section.label }}
            </p>

            <div
              v-for="command in section.commands"
              :id="optionId(indexOf(command))"
              :key="command.id"
              class="e-palette__option"
              role="option"
              :aria-selected="indexOf(command) === activeIndex"
              :data-active="indexOf(command) === activeIndex"
              @click="choose(command)"
              @mousemove="activeIndex = indexOf(command)"
            >
              <EIcon v-if="command.icon" :name="command.icon" :size="15" class="e-palette__option-icon" />
              <span class="e-palette__option-body">
                <span class="e-palette__option-label">{{ command.label }}</span>
                <span v-if="command.description" class="e-palette__option-description">
                  {{ command.description }}
                </span>
              </span>
              <span v-if="command.hint" class="e-palette__option-hint">{{ command.hint }}</span>
            </div>
          </template>
        </div>

        <footer v-if="showFooter" class="e-palette__footer">
          <span
            ><kbd class="e-kbd">↑</kbd><kbd class="e-kbd">↓</kbd> {{ strings.paletteNavigateHint }}</span
          >
          <span><kbd class="e-kbd">{{ enterHint }}</kbd> {{ strings.paletteRunHint }}</span>
          <span
            ><kbd class="e-kbd">{{ strings.paletteEscKey }}</kbd> {{ strings.paletteCloseHint }}</span
          >
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<style>
.e-palette__overlay {
  position: fixed;
  inset: 0;
  z-index: var(--erpx-z-overlay);
  background-color: var(--erpx-color-scrim);
}

.e-palette {
  position: fixed;
  inset-block-start: 12vh;
  inset-inline-start: 50%;
  transform: translateX(-50%);
  z-index: var(--erpx-z-dialog);
  display: flex;
  flex-direction: column;
  width: calc(100vw - var(--erpx-space-8));
  max-width: 40rem;
  max-height: 60vh;
  background-color: var(--erpx-color-surface-overlay);
  border: 1px solid var(--erpx-color-border-subtle);
  border-radius: var(--erpx-radius-lg);
  box-shadow: var(--erpx-shadow-overlay);
  overflow: hidden;
}

.e-palette:focus-visible {
  outline: none;
}

.e-palette__search {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  padding-inline: var(--erpx-space-3);
  border-block-end: 1px solid var(--erpx-color-border-subtle);
}

.e-palette__search-icon {
  color: var(--erpx-color-text-muted);
}

.e-palette__input {
  flex: 1;
  min-width: 0;
  height: 48px;
  background: none;
  border: none;
  color: var(--erpx-color-text-primary);
  font-family: var(--erpx-font-ui);
  font-size: var(--erpx-text-lg);
}

/*
 * The input is the palette's only field and holds focus the whole time it is
 * open, so the global focus ring would just box the header strip. Focus is
 * shown on the strip's bottom border instead.
 */
.e-palette__input:focus,
.e-palette__input:focus-visible {
  outline: none;
  box-shadow: none;
}

.e-palette__search:focus-within {
  border-block-end-color: var(--erpx-color-accent-border);
}

.e-palette__input::placeholder {
  color: var(--erpx-color-text-disabled);
}

.e-palette__list {
  flex: 1;
  min-height: 0;
  padding: var(--erpx-space-1);
  overflow-y: auto;
}

.e-palette__empty {
  padding: var(--erpx-space-6) var(--erpx-space-3);
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-sm);
  text-align: center;
}

.e-palette__group {
  padding: var(--erpx-space-3) var(--erpx-space-2) var(--erpx-space-1);
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
}

.e-palette__option {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-2);
  min-height: var(--erpx-row-h);
  padding-inline: var(--erpx-space-2);
  border-radius: var(--erpx-radius-sm);
  color: var(--erpx-color-text-primary);
  font-size: var(--erpx-text-sm);
  cursor: pointer;
}

.e-palette__option[data-active='true'] {
  background-color: var(--erpx-color-surface-selected);
  color: var(--erpx-color-accent-text);
}

.e-palette__option-icon {
  color: var(--erpx-color-text-muted);
}

.e-palette__option-body {
  display: flex;
  flex-direction: column;
  gap: 1px;
  flex: 1;
  min-width: 0;
}

.e-palette__option-label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.e-palette__option-description {
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
}

.e-palette__option-hint {
  color: var(--erpx-color-text-muted);
  font-family: var(--erpx-font-mono);
  font-size: var(--erpx-text-2xs);
  white-space: nowrap;
}

.e-palette__footer {
  display: flex;
  align-items: center;
  gap: var(--erpx-space-4);
  padding: var(--erpx-space-2) var(--erpx-space-3);
  border-block-start: 1px solid var(--erpx-color-border-subtle);
  background-color: var(--erpx-color-surface-sunken);
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
}

.e-kbd {
  display: inline-block;
  min-width: 18px;
  margin-inline-end: 3px;
  padding-inline: 4px;
  background-color: var(--erpx-color-surface-raised);
  border: 1px solid var(--erpx-color-border-default);
  border-radius: var(--erpx-radius-sm);
  font-family: var(--erpx-font-mono);
  font-size: var(--erpx-text-2xs);
  line-height: 16px;
  text-align: center;
}

.e-visually-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
  border: 0;
}
</style>
